import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import api from '../lib/api';
import { PaginatedProcessSyncHistoryDto, ProcessMovementDto } from '@prometeus/contracts';
import { MovementDetailModal } from '../components/MovementDetailModal';
import { AddMovementModal } from '../components/AddMovementModal';
import { ArchivedProcessBlockModal } from '../components/ArchivedProcessBlockModal';
import { usePermissions } from '../hooks/usePermissions';

export const Route = createFileRoute(
  '/w/$workspaceId/clients/$clientId/processes/$processId/sync-history'
)({
  component: SyncHistoryTab,
});

function SyncHistoryTab() {
  const { workspaceId, clientId, processId } = Route.useParams();
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission('processes.update');
  const [page, setPage] = useState(1);
  const [selectedMovement, setSelectedMovement] = useState<ProcessMovementDto | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showArchivedBlockModal, setShowArchivedBlockModal] = useState(false);
  const limit = 5;

  const { data: process } = useQuery({
    queryKey: ['processes', workspaceId, clientId, processId],
    queryFn: async () => {
      const response = await api.get(`/clients/${clientId}/processes/${processId}`);
      return response.data.data;
    },
    enabled: !!processId && !!clientId,
  });

  const { data: history, isLoading } = useQuery<PaginatedProcessSyncHistoryDto>({
    queryKey: ['process-sync-history', workspaceId, processId, page, limit],
    queryFn: async () => {
      const response = await api.get(`/clients/${clientId}/processes/${processId}/sync-history`, {
        params: { page, limit },
      });
      return response.data.data;
    },
    enabled: !!processId && !!clientId,
  });

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'unread':
        return { bg: '#EAB30820', text: '#EAB308' };
      case 'read':
        return { bg: '#2563EB20', text: '#2563EB' };
      case 'deleted':
        return { bg: '#6B728020', text: '#6B7280' };
      default:
        return { bg: '#6B728020', text: '#6B7280' };
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'unread':
        return 'Não lida';
      case 'read':
        return 'Lida';
      case 'deleted':
        return 'Deletada';
      default:
        return status;
    }
  };

  const handleMovementClick = (movement: ProcessMovementDto) => {
    setSelectedMovement(movement);
    setShowDetailModal(true);
  };

  const totalPages = history?.totalPages || 1;

  if (isLoading) {
    return (
      <div className="pt-4 pb-8">
        <div
          className="bg-white p-4"
          style={{ fontFamily: 'Poppins, sans-serif', borderRadius: 15, boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)' }}
        >
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 rounded-[10px]" style={{ backgroundColor: 'rgba(6, 20, 27, 0.06)' }} />
            ))}
          </div>
          <p className="mt-4" style={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, color: '#4A5C6A' }}>
            Carregando histórico...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-4 pb-8">
      <div
        className="bg-white p-4 flex flex-col"
        style={{ fontFamily: 'Poppins, sans-serif', borderRadius: 15, boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)' }}
      >
        {/* Header: ícone + título + botão adicionar (padrão do projeto) */}
        <div
          className="flex items-center justify-between mb-4 pb-4"
          style={{ borderBottom: '1px solid rgba(6, 20, 27, 0.08)' }}
        >
          <div className="flex items-center gap-2">
            <img src="/history.svg" alt="" width={24} height={24} className="flex-shrink-0" />
            <h2 className="text-[16px]" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400, color: '#06141B' }}>
              Histórico de Sincronizações
            </h2>
          </div>
          {canUpdate && (
            <button
              type="button"
              onClick={() => (process?.archivedAt ? setShowArchivedBlockModal(true) : setShowAddModal(true))}
              className="flex items-center justify-center w-8 h-8 rounded-[10px] transition-opacity hover:opacity-90 flex-shrink-0"
              style={{
                fontFamily: 'Poppins, sans-serif',
                backgroundColor: '#06141B',
                color: '#F9FAFB',
              }}
              title="Adicionar Histórico"
              aria-label="Adicionar Histórico"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>

        {/* Movimentações */}
        <div>
          <h3
            className="mb-4"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: 14, fontWeight: 400, color: '#06141B' }}
          >
            Movimentações do Processo
          </h3>
          {history?.movements && history.movements.length > 0 ? (
            <>
              <div className="space-y-3">
                {history.movements.map((movement: ProcessMovementDto) => {
                  const statusStyle = getStatusStyle(movement.status);
                  return (
                    <div
                      key={movement.id}
                      onClick={() => handleMovementClick(movement)}
                      className="flex rounded-xl transition-shadow cursor-pointer"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
                      }}
                    >
                      <div className="flex-1 min-w-0 p-4">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h4
                            className="font-medium"
                            style={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, color: '#06141B' }}
                          >
                            {movement.movementType || 'Movimentação'}
                          </h4>
                          <span
                            className="px-2.5 py-1 rounded-[10px] whitespace-nowrap"
                            style={{
                              fontFamily: 'Poppins, sans-serif',
                              fontSize: 10,
                              fontWeight: 400,
                              backgroundColor: statusStyle.bg,
                              color: statusStyle.text,
                            }}
                          >
                            {getStatusLabel(movement.status)}
                          </span>
                          {movement.treatedAt && (
                            <span
                              className="px-2.5 py-1 rounded-[10px] whitespace-nowrap"
                              style={{
                                fontFamily: 'Poppins, sans-serif',
                                fontSize: 10,
                                fontWeight: 400,
                                backgroundColor: '#16A34A20',
                                color: '#16A34A',
                              }}
                            >
                              Tratada
                            </span>
                          )}
                        </div>
                        <p
                          className="mb-1"
                          style={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, color: '#4A5C6A' }}
                        >
                          <span className="font-medium">Data:</span> {formatDate(movement.movementDate)}
                        </p>
                        {movement.description && (
                          <p
                            className="mt-2 line-clamp-2"
                            style={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, color: '#06141B' }}
                          >
                            {movement.description.length > 200
                              ? `${movement.description.substring(0, 200)}...`
                              : movement.description}
                          </p>
                        )}
                        {movement.treatedAt && movement.treatedBy && (
                          <p
                            className="mt-2"
                            style={{ fontFamily: 'Poppins, sans-serif', fontSize: 11, color: '#4A5C6A' }}
                          >
                            Tratada em {formatDate(movement.treatedAt)} por {movement.treatedBy.name}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Paginação — mesmo padrão da lista de processos */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-4">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      backgroundColor: '#F9FAFB',
                      color: '#2D3748',
                    }}
                    onMouseEnter={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.backgroundColor = '#F3F4F6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#F9FAFB';
                    }}
                  >
                    &lt;
                  </button>
                  <span className="px-4 py-2" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                    Página {page} de {totalPages} ({(history?.total ?? 0)} {(history?.total ?? 0) === 1 ? 'movimentação' : 'movimentações'})
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      backgroundColor: '#F9FAFB',
                      color: '#2D3748',
                    }}
                    onMouseEnter={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.backgroundColor = '#F3F4F6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#F9FAFB';
                    }}
                  >
                    &gt;
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-500" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
              Nenhuma movimentação encontrada
            </p>
          )}
        </div>
      </div>

      {/* Modal de Detalhes */}
      <MovementDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedMovement(null);
        }}
        movement={selectedMovement}
      />

      {/* Modal de Adicionar */}
      <AddMovementModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        processId={processId}
        clientId={clientId}
        workspaceId={workspaceId}
      />

      {/* Modal de processo arquivado (bloqueio) */}
      <ArchivedProcessBlockModal
        isOpen={showArchivedBlockModal}
        onClose={() => setShowArchivedBlockModal(false)}
        workspaceId={workspaceId}
        clientId={clientId}
        processId={processId}
      />
    </div>
  );
}
